import React, { useMemo } from "react";
import { useFormContext, Controller } from "react-hook-form";
import {
  REGEX_EMAIL,
  REGEX_PHONE,
  REGEX_PHONE_INVERSE,
  REGEX_POST_NUMBER
} from "@helpers/_constants";
import { useNotification } from "@hooks/useNotification";
import { Users } from "@services/all.services";
import debounce from "lodash/debounce";
import { useTranslation } from "react-i18next";
import { useUserPermission } from "@hooks/users";
import { Input } from "@components/tailwindComponents";
import { convertToNumericString } from "@helpers/_functions";

function SharedEmailAndPhone(props) {
  const { readOnly = false, isEnterprisePage } = props;
  const { control } = useFormContext();
  const { showError } = useNotification();
  const { t: mainTranslator } = useTranslation("pages.employés.infoPers");
  const { canAccessEmails, canAccessPhones } = useUserPermission();

  const readOnlyEmails = useMemo(() => {
    return isEnterprisePage ? readOnly : (readOnly && !canAccessEmails);
  }, [canAccessEmails, readOnly, isEnterprisePage])

  const readOnlyPhones = useMemo(() => {
    return isEnterprisePage ? readOnly : (readOnly && !canAccessPhones);
  }, [canAccessPhones, readOnly, isEnterprisePage])

  function emailAlreadyExist(email) {
    return new Promise((resolve) => {
      validateEmail(email, resolve);
    });
  }

  const validateEmail = debounce(async (email, callback) => {
    let requestData = { email, userId: props.employeeId };

    const response = await Users.verifyExistEmail(requestData);
    if (response?.userAlreadyExist) {
      showError("already_exists", { object: "emailUpper" });
      callback(false);
    }

    callback(true);
  }, 500);

  return (
    <div className="arh-pr-4 arh-pl-[5.8rem] arh-pb-8 arh-w-full arh-flex arh-items-center arh-flex-wrap tablet-850:arh-items-start tablet-850:arh-justify-start tablet-850:arh-gap-x-5 tablet-850:arh-gap-y-7 tablet-850:!arh-pl-[2.5rem] tablet-850:!arh-pr-[1rem]">
      {/* arh-flex-[0.25_0.25_0] */}
      <div className="arh-flex-1 arh-mr-[1.875rem] 3xl:arh-flex-none 3xl:arh-w-[43%] tablet-850:!arh-mr-0 tablet-850:arh-min-w-0 tablet-850:!arh-w-[calc(50%_-_0.625rem)] tablet-850:!arh-flex-[0_0_calc(50%_-_0.625rem)]">
        <Controller
          control={control}
          name="emails.personalEmail"
          defaultValue=""
          rules={{
            required: true,
            pattern: REGEX_EMAIL,
            validate: { emailAlreadyExist }
          }}
          render={({ field: { onChange, value }, fieldState: { invalid } }) => (
            <Input
              label={mainTranslator("Courriel de correspondance*")}
              placeholder={mainTranslator("Courriel de correspondance*")}
              type="email"
              onChange={(e) => onChange(e.target.value.toLowerCase())}
              value={value}
              invalid={invalid}
              disabled={readOnlyEmails}
            />
          )}
        />
      </div>
      <div className="arh-flex-1 arh-mr-[1.875rem] 3xl:arh-flex-none 3xl:arh-w-[43%] tablet-850:!arh-mr-0 tablet-850:arh-min-w-0 tablet-850:!arh-w-[calc(50%_-_0.625rem)] tablet-850:!arh-flex-[0_0_calc(50%_-_0.625rem)]">
        <Controller
          control={control}
          name="emails.professionalEmail"
          defaultValue=""
          rules={{ pattern: REGEX_EMAIL }}
          render={({ field: { onChange, value }, fieldState: { invalid } }) => (
            <Input
              label={mainTranslator("Autre courriel")}
              placeholder={mainTranslator("Autre courriel")}
              type="email"
              onChange={(e) => onChange(e.target.value.toLowerCase())}
              value={value}
              invalid={invalid}
              disabled={readOnlyEmails}
            />
          )}
        />
      </div>
      <div className="arh-flex-1 arh-mr-[1.875rem] arh-flex arh-items-center 3xl:arh-flex-none 3xl:arh-w-[43%] tablet-850:!arh-mr-0 tablet-850:arh-min-w-0 tablet-850:!arh-w-[calc(50%_-_0.625rem)] tablet-850:!arh-flex-[0_0_calc(50%_-_0.625rem)] tablet-850:!arh-items-start">
        <div className="arh-flex-1 ">
          <Controller
            control={control}
            name="emails.professionalPhone"
            rules={{ pattern: REGEX_PHONE }}
            defaultValue=""
            render={({
              field: { onChange, value },
              fieldState: { invalid }
            }) => (
              <Input
                label={mainTranslator("Téléphone Professionnel")}
                placeholder={mainTranslator("Téléphone Professionnel")}
                type="text"
                onChange={(e) => {
                  const cleanedValue = e.target.value.replace(
                    REGEX_PHONE_INVERSE,
                    ""
                  );
                  onChange(cleanedValue);
                }}
                value={value}
                disabled={readOnlyPhones}
                invalid={invalid}
              />
            )}
          />
        </div>
        <div className="arh-w-[33%] arh-pl-4 arh-mb-0 tablet-850:!arh-w-[36%] tablet-850:!arh-pl-3">
          <Controller
            control={control}
            name="emails.professionalPhonePostNumber"
            defaultValue=""
            rules={{
              validate: {
                validationForPostNumber: (value) =>
                   value === "" || (value && REGEX_POST_NUMBER.test(value))
              }
            }}
            render={({
              field: { onChange, value, name },
              fieldState: { invalid }
            }) => (
              <Input
                value={value}
                onChange={(e) => {
                  onChange(convertToNumericString(e.target.value));
                }}
                name={name}
                label={mainTranslator("extension")}
                placeholder={mainTranslator("000")}
                invalid={invalid}
                disabled={readOnlyPhones}
              />
            )}
          />
        </div>
      </div>
      <div className="arh-flex-1 arh-mr-[1.875rem] arh-flex arh-items-center 3xl:arh-flex-none 3xl:arh-w-[43%] tablet-850:!arh-mr-0 tablet-850:arh-min-w-0 tablet-850:!arh-w-[calc(50%_-_0.625rem)] tablet-850:!arh-flex-[0_0_calc(50%_-_0.625rem)] tablet-850:!arh-items-start">
        <div className="arh-flex-1 ">
          <Controller
            control={control}
            name="emails.personalPhone"
            rules={{ pattern: REGEX_PHONE }}
            defaultValue=""
            render={({
              field: { onChange, value },
              fieldState: { invalid }
            }) => (
              <Input
                label={mainTranslator("Téléphone personnel")}
                placeholder={mainTranslator("Téléphone personnel")}
                type="text"
                onChange={(e) => {
                  const cleanedValue = e.target.value.replace(
                    REGEX_PHONE_INVERSE,
                    ""
                  );
                  onChange(cleanedValue);
                }}
                value={value}
                disabled={readOnlyPhones}
                invalid={invalid}
              />
            )}
          />
        </div>
        <div className="arh-w-[33%] arh-pl-4 arh-mb-0 tablet-850:!arh-w-[36%] tablet-850:!arh-pl-3">
          <Controller
            control={control}
            name="emails.personalPhonePostNumber"
            defaultValue=""
            rules={{
              validate: {
                validationForPostNumber: (value) =>
                  value === "" || (value && REGEX_POST_NUMBER.test(value))
              }
            }}
            render={({
              field: { onChange, value, name },
              fieldState: { invalid }
            }) => (
              <Input
                value={value}
                onChange={(e) => {
                  onChange(convertToNumericString(e.target.value));
                }}
                name={name}
                label={mainTranslator("extension")}
                placeholder={mainTranslator("000")}
                invalid={invalid}
                disabled={readOnlyPhones}
              />
            )}
          />
        </div>
      </div>
    </div>
  );
}

export default SharedEmailAndPhone;
